import React from 'react';
import {PropTypes} from 'prop-types';
import {Link} from '../../shared/link';
import {MakeApiCall, WriteConsoleErrors, ReplaceUrl, HumanFileSize, DisplayErrors, DisplaySuccessMessage, GetMomentTimeZoneName} from '../../shared/utils';
import {ErrorsBlockComponent} from '../../shared/errorsBlockComponent';
import {RefreshButtonComponent} from '../../shared/refreshButtonComponent';
import {LoadingIndicatorComponent} from '../../shared/loadingIndicatorComponent';
import {AccountComponent} from '../AccountsListComponent/account';
import {InfoItem} from '../AccountInfo/infoitem';
import {Pagination} from '../../shared/Pagination';
import {DateTimeItem} from '../AccountInfo/datetimeitem';
import {UrlBuilder, APPLICATION_TYPE} from '../../app.defs';
import {EditNotesDialogComponent} from '../../shared/EditNotesDialog';
import {PageHeader} from '../../shared/PageHeader';

import {Controlled as CodeMirror} from 'react-codemirror2';
import "codemirror/mode/xml/xml.js";
import "codemirror/lib/codemirror.css";
import '../../components/job/codecontrol.scss';
import { ButtonBusyIndicatorComponent } from '../../shared/buttonBusyIndicatorComponent';
import { JobOptionsDlgComponent } from '../../components/job/jobOptionsDlg';
import { ItemPopupDescriptionComponent } from '../../shared/itemPopupDescriptionComponent';

import './styles.scss';

const AGENTINFO_ACTION = {
    NONE: 0,
    LOADING: 1,
    EDITNOTES: 2,
    REQUESTPROPS_DLG: 3,
}

const AGENTMESSAGE_TYPE = {
    ActivateLogging: 14001,
    DeactivateLogging: 14002,
    UploadLogs: 14003,
    CleanupLog: 14004,
    UploadEventLogs: 14006,
    UploadRoutinesDataBase: 14005,
    UploadContextDataBase: 14007,
}
AGENTMESSAGE_TYPE.Timeout = {
    Default: 60,
    [AGENTMESSAGE_TYPE.ActivateLogging]: 60,
    [AGENTMESSAGE_TYPE.DeactivateLogging]: 60,
    [AGENTMESSAGE_TYPE.CleanupLog]: 60,
    [AGENTMESSAGE_TYPE.UploadLogs]: 5 * 60,
    [AGENTMESSAGE_TYPE.UploadEventLogs]: 5 * 60,
    [AGENTMESSAGE_TYPE.UploadRoutinesDataBase]: 5 * 60,
    [AGENTMESSAGE_TYPE.UploadContextDataBase]: 5 * 60,
}
AGENTMESSAGE_TYPE.Params = {
    [AGENTMESSAGE_TYPE.ActivateLogging]: [
        {
            Id: 'Configuration', 
            IsAdditional: true,
            ValueType: 'text', 
            Name: 'Configuration', 
            DefaultValue: '',
            Description: 'XML-style configuration for NLog. \nIf empty, the default configuration is used.'
        },
    ],
    [AGENTMESSAGE_TYPE.UploadEventLogs]: [
        {
            Id: 'LogName', 
            IsAdditional: true,
            ValueType: 'string', 
            Name: 'Log name', 
            DefaultValue: 'Application'
        },
        {
            Id: 'Source',
            IsAdditional: true,
            ValueType: 'string', 
            Name: 'Source', 
            DefaultValue: '',
            Description: 'If specified, returns only log entries where Source contains the specified substring'
        },
        {
            Id: 'Message',
            IsAdditional: true,
            ValueType: 'string', 
            Name: 'Message', 
            DefaultValue: '',
            Description: 'If specified, returns only log entries where Message contains the specified substring'
        },
        {
            Id: 'Count',
            IsAdditional: true,
            ValueType: 'int', 
            Name: 'Max entries count', 
            MinValue: 16,
            MaxValue: 16384,
            DefaultValue: 64
        },
    ]
}

// class JsonDisplayDlg extends ModalBase {
//     constructor(props, ...others) {
//         super(props, ...others);

// }

class ConnectionsTable extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            agentId: props.agentId,
            isLoaded: false,
            isLoading: false,
            errors: null,
            connections: [],
            requestId: 0,
            pageNum: 1,
            pageSize: props.pageSize,
            totalCount: 0,
            serverTypeId: null,
            deletedStateId: 0, // not deleted
            usageStateId: 0, // used only
        }

        this.doLoad = this.doLoad.bind(this);
        this.handleServerTypeChange = this.handleServerTypeChange.bind(this);
        this.handleDeletedStateChange = this.handleDeletedStateChange.bind(this);
        this.handleUsageStateChange = this.handleUsageStateChange.bind(this);
        this.handleRefreshClick = this.handleRefreshClick.bind(this);
        this.getConnectionInfo = this.getConnectionInfo.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps) {
        const self = this;

        if (self.props.agentId != prevProps.agentId) {
            self.setState({agentId: self.props.agentId}, self.doLoad);
        }
        else if (self.props.versionStamp !== prevProps.versionStamp) {
            self.doLoad();
        }
    }

    handleServerTypeChange(e) {
        const self = this;
        const v = parseInt(e.target.value);
        const serverTypeId = isNaN(v) ? null : v;

        if (serverTypeId != self.state.serverTypeId) {
            self.setState({serverTypeId: serverTypeId}, self.doLoad);
        }
    }

    handleDeletedStateChange(e) {
        const self = this;
        const v = parseInt(e.target.value);
        const deletedStateId = isNaN(v) ? null : v;

        if (deletedStateId != self.state.deletedStateId) {
            self.setState({deletedStateId: deletedStateId}, self.doLoad);
        }
    }

    handleUsageStateChange(e) {
        const self = this;
        const v = parseInt(e.target.value);
        const usageStateId = isNaN(v) ? null : v;

        if (usageStateId != self.state.usageStateId) {
            self.setState({usageStateId: usageStateId}, self.doLoad);
        }
    }

    handleRefreshClick() {
        this.doLoad();
    }

    doLoad() {
        const self = this;
        if (!self._isMounted) {
            return;
        }
        const requestId = self.state.requestId + 1;

        const data = {
            AgentId: self.state.agentId, 
            pn: self.state.pageNum, 
            ps: self.state.pageSize, 
            st: self.state.serverTypeId, 
            ds: self.state.deletedStateId, 
            us: self.state.usageStateId
        };

        new Promise(resolve => {
            self.setState({isLoading: true, errors: null, requestId: requestId}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgentConnections',
                            data: data,
                            dataType: 'json',
                            contentType: "application/json",
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted || (self.state.requestId != requestId)
                )
            }
        })
        .then(response => {
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({
                    isLoading: false,
                    isLoaded: true,
                    connections: response.Connections,
                    pageNum: response.PageNum,
                    pageSize: response.PageSize,
                    totalCount: response.TotalCount
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({isLoading: false, errors: errors})
            }
        });
    }

    getConnectionInfo(connectionId) {
        const self = this;

        if (self._isMounted && self.state.isLoaded) {
            const connection = self.state.connections.find(p => p.Id == connectionId);

            if (connection != null) {

                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgentConnectionDbmsInfo',
                            data: {id: connectionId},
                            dataType: 'json',
                            contentType: "application/json",
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                )
                .then(requestResult => {
                    if (self._isMounted) {
                        return `<div class="textualinfo">${requestResult}</div>`;
                    }
                });
            }
        }

        return null;
    }

    render() {
        const self = this;
        const {isLoading, isLoaded, errors, pageNum, pageSize, totalCount, connections, serverTypeId, deletedStateId, usageStateId} = self.state;
        const {timeZoneId, serverTypes, deletedStates, usageStates} = self.props;

        const pagesCount = Math.ceil(totalCount / Math.max(pageSize, 1));
        const hasDeleted = connections.some(p => Boolean(p.DeletedAt));

        return (
            <div>
                <ErrorsBlockComponent errors={errors} unstyled />
                <div className="d-flex">
                    <div className="form-group newstyle mr-2">
                        <select
                            className="custom-select w-auto"
                            value={serverTypeId == null ? '' : serverTypeId}
                            onChange={self.handleServerTypeChange}
                            disabled={isLoading}
                        >
                            <option value="">any</option>
                            {serverTypes.map(p => <option key={`mt-${p.Id}`} value={p.Id}>{p.Name}</option>)}
                        </select>
                        <label>DBMS type</label>
                    </div>
                    <div className="form-group newstyle mr-2">
                        <select
                            className="custom-select w-auto"
                            value={deletedStateId == null ? '' : deletedStateId}
                            onChange={self.handleDeletedStateChange}
                            disabled={isLoading}
                        >
                            <option value="">any</option>
                            {deletedStates.map(p => <option key={`mt-${p.Id}`} value={p.Id}>{p.Name}</option>)}
                        </select>
                        <label>Deleted state</label>
                    </div>
                    <div className="form-group newstyle mr-2">
                        <select
                            className="custom-select w-auto"
                            value={usageStateId == null ? '' : usageStateId}
                            onChange={self.handleUsageStateChange}
                            disabled={isLoading}
                        >
                            <option value="">any</option>
                            {usageStates.map(p => <option key={`mt-${p.Id}`} value={p.Id}>{p.Name}</option>)}
                        </select>
                        <label>Usage state</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={isLoading} 
                        animated={isLoading} 
                        onClick={self.handleRefreshClick} 
                    />
                </div>
                <div className="fs-xs">Found:&nbsp;{totalCount}</div>
                <table className="agentconnectionslist newstyle table-striped-even table-hover">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Type</th>
                            <th>Name</th>
                            <th>Created</th>
                            {hasDeleted && <th>Deleted</th>}
                            <th>Jobs</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoaded && connections.map(p => {
                            let rowClass = '';

                            if (Boolean(p.DeletedAt)) {
                                rowClass = 'danger';
                            }

                            return (
                                <tr key={p.Id} className={rowClass}>
                                    <td className="text-right">
                                        <ItemPopupDescriptionComponent
                                            popoverClassName="popover-white"
                                            placement="bottom"
                                            text={() => self.getConnectionInfo(p.Id)}
                                            showCopyButton
                                            showIfEmpty
                                        >
                                            {p.Id}
                                        </ItemPopupDescriptionComponent>
                                    </td>
                                    <td className="text-nowrap">{p.ServerTypeName}</td>
                                    <td className="w-100 text-break-word">{p.Name}</td>
                                    <td className="text-nowrap"><DateTimeItem utcDate={p.CreatedAt} timeZoneId={timeZoneId} /></td>
                                    {hasDeleted && <td className="text-nowrap"><DateTimeItem utcDate={p.DeletedAt} timeZoneId={timeZoneId} /></td>}
                                    <td className="text-right">{p.JobsCount}</td>
                                </tr>
                            )
                        })}
                        {isLoading && !isLoaded && <tr><td colSpan={5}><LoadingIndicatorComponent visible /></td></tr>}
                    </tbody>
                </table>
                {(pagesCount > 1) && 
                    <div className="pt-3">
                        <Pagination 
                            className="justify-content-end"
                            pagesCount={pagesCount}
                            pageNum={pageNum}
                            size="sm"
                            disabled={isLoading}
                            onPageSelect={pn => self.setState({pageNum: pn}, self.doLoad)}
                        />
                    </div>
                }
            </div>
        )
    }
}
ConnectionsTable.propTypes = {
    pageSize: PropTypes.number.isRequired,
    timeZoneId: PropTypes.string.isRequired,
    serverTypes: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired})).isRequired,
    deletedStates: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired})).isRequired,
    usageStates: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired})).isRequired,
}
ConnectionsTable.defaultProps = {
    pageSize: 20,
}

class JobsTable extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            agentId: props.agentId,
            isLoaded: false,
            isLoading: false,
            errors: null,
            jobs: [],
            requestId: 0,
            pageNum: 1,
            pageSize: props.pageSize,
            totalCount: 0,
            jobTypeId: null,
            deletedStateId: 0, // not deleted
        }

        this.doLoad = this.doLoad.bind(this);
        this.handleJobTypeChange = this.handleJobTypeChange.bind(this);
        this.handleDeletedStateChange = this.handleDeletedStateChange.bind(this);
        this.handleRefreshClick = this.handleRefreshClick.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps) {
        const self = this;

        if (self.props.agentId != prevProps.agentId) {
            self.setState({agentId: self.props.agentId}, self.doLoad);
        }
        else if (self.props.versionStamp !== prevProps.versionStamp) {
            self.doLoad();
        }
    }

    handleJobTypeChange(e) {
        const self = this;
        const v = parseInt(e.target.value);
        const jobTypeId = isNaN(v) ? null : v;

        if (jobTypeId != self.state.jobTypeId) {
            self.setState({jobTypeId: jobTypeId}, self.doLoad);
        }
    }

    handleDeletedStateChange(e) {
        const self = this;
        const v = parseInt(e.target.value);
        const deletedStateId = isNaN(v) ? null : v;

        if (deletedStateId != self.state.deletedStateId) {
            self.setState({deletedStateId: deletedStateId}, self.doLoad);
        }
    }

    handleRefreshClick() {
        this.doLoad();
    }

    doLoad() {
        const self = this;
        if (!self._isMounted) {
            return;
        }
        const requestId = self.state.requestId + 1;

        const data = {
            AgentId: self.state.agentId, 
            pn: self.state.pageNum, 
            ps: self.state.pageSize, 
            jt: self.state.jobTypeId, 
            ds: self.state.deletedStateId, 
        };

        new Promise(resolve => {
            self.setState({isLoading: true, errors: null, requestId: requestId}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgentJobs',
                            data: data,
                            dataType: 'json',
                            contentType: "application/json",
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted || (self.state.requestId != requestId)
                )
            }
        })
        .then(response => {
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({
                    isLoading: false,
                    isLoaded: true,
                    jobs: response.Jobs,
                    pageNum: response.PageNum,
                    pageSize: response.PageSize,
                    totalCount: response.TotalCount
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({isLoading: false, errors: errors})
            }
        });
    }

    render() {
        const self = this;
        const {isLoading, isLoaded, errors, pageNum, pageSize, totalCount, jobs, jobTypeId, deletedStateId} = self.state;
        const {accountId, accountName, timeZoneId, jobTypes, deletedStates} = self.props;

        const pagesCount = Math.ceil(totalCount / Math.max(pageSize, 1));
        const hasDeleted = jobs.some(p => Boolean(p.DeletedAt));

        return (
            <div>
                <ErrorsBlockComponent errors={errors} unstyled />
                <div className="d-flex">
                    <div className="form-group newstyle mr-2">
                        <select
                            className="custom-select w-auto"
                            value={jobTypeId ?? ''}
                            onChange={self.handleJobTypeChange}
                            disabled={isLoading}
                        >
                            <option value="">any</option>
                            {jobTypes.map(p => <option key={`mt-${p.Id}`} value={p.Id}>{p.Name}</option>)}
                        </select>
                        <label>Job type</label>
                    </div>
                    <div className="form-group newstyle mr-2">
                        <select
                            className="custom-select w-auto"
                            value={deletedStateId ?? ''}
                            onChange={self.handleDeletedStateChange}
                            disabled={isLoading}
                        >
                            <option value="">any</option>
                            {deletedStates.map(p => <option key={`mt-${p.Id}`} value={p.Id}>{p.Name}</option>)}
                        </select>
                        <label>Deleted state</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={isLoading} 
                        animated={isLoading} 
                        onClick={self.handleRefreshClick} 
                    />
                </div>
                <div className="fs-xs">Found:&nbsp;{totalCount}</div>
                <table className="agentjobslist newstyle table-striped-even table-hover">
                    <thead>
                        <tr>
                            <th>#</th>
                            {/* <th colSpan={2}>Type</th> */}
                            <th colSpan={2}>Name</th>
                            <th>Created</th>
                            {hasDeleted && <th>Deleted</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoaded && jobs.map(p => {
                            let rowClass = '';

                            if (Boolean(p.DeletedAt)) {
                                rowClass = 'danger';
                            }

                            return (
                                <tr key={p.Id} className={rowClass}>
                                    <td className="text-right">
                                        <AccountComponent 
                                            id={accountId} 
                                            name={p.Id + ''} 
                                            disabled={isLoading} 
                                            showLoginAs
                                            url={UrlBuilder.JobView({JobId: p.Id})} 
                                            loginAsTooltip={{title: `Login As ${accountName} and go to job's page`}}
                                        />
                                    </td>
                                    <td className="text-right pr-0"><i className={p.JobTypeIconClassName}/></td>
                                    {/* <td className="text-nowrap">{p.JobTypeName}</td> */}
                                    <td className="w-100 text-break-word">{p.Name}</td>
                                    <td className="text-nowrap"><DateTimeItem utcDate={p.CreatedAt} timeZoneId={timeZoneId} /></td>
                                    {hasDeleted && <td className="text-nowrap"><DateTimeItem utcDate={p.DeletedAt} timeZoneId={timeZoneId} /></td>}
                                </tr>
                            )
                        })}
                        {isLoading && !isLoaded && <tr><td colSpan={5}><LoadingIndicatorComponent visible /></td></tr>}
                    </tbody>
                </table>
                {(pagesCount > 1) && 
                    <div className="pt-3">
                        <Pagination 
                            className="justify-content-end"
                            pagesCount={pagesCount}
                            pageNum={pageNum}
                            size="sm"
                            disabled={isLoading}
                            onPageSelect={pn => self.setState({pageNum: pn}, self.doLoad)}
                        />
                    </div>
                }
            </div>
        )
    }
}
JobsTable.propTypes = {
    pageSize: PropTypes.number.isRequired,
    timeZoneId: PropTypes.string.isRequired,
    jobTypes: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired})).isRequired,
    deletedStates: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired})).isRequired,
}
JobsTable.defaultProps = {
    pageSize: 20,
}

class AgentRunsTable extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            agentId: props.agentId,
            isLoaded: false,
            isLoading: false,
            errors: null,
            runs: [],
            requestId: 0,
            pageNum: 1,
            pageSize: props.pageSize,
            totalCount: 0,
        }

        this.doLoad = this.doLoad.bind(this);
        this.handleRefreshClick = this.handleRefreshClick.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps) {
        const self = this;

        if (self.props.agentId != prevProps.agentId) {
            self.setState({agentId: self.props.agentId}, self.doLoad);
        }
        else if (self.props.versionStamp !== prevProps.versionStamp) {
            self.doLoad();
        }
    }

    handleRefreshClick() {
        this.doLoad();
    }

    doLoad() {
        const self = this;
        if (!self._isMounted) {
            return;
        }
        const requestId = self.state.requestId + 1;

        const data = {
            AgentId: self.state.agentId, 
            pn: self.state.pageNum, 
            ps: self.state.pageSize, 
        };

        new Promise(resolve => {
            self.setState({isLoading: true, errors: null, requestId: requestId}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgentRuns',
                            data: data,
                            dataType: 'json',
                            contentType: "application/json",
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted || (self.state.requestId != requestId)
                )
            }
        })
        .then(response => {
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({
                    isLoading: false,
                    isLoaded: true,
                    runs: response.Runs,
                    pageNum: response.PageNum,
                    pageSize: response.PageSize,
                    totalCount: response.TotalCount
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({isLoading: false, errors: errors})
            }
        });
    }

    render() {
        const self = this;
        const {isLoading, isLoaded, errors, pageNum, pageSize, totalCount, runs} = self.state;
        const {timeZoneId} = self.props;

        const pagesCount = Math.ceil(totalCount / Math.max(pageSize, 1));

        return (
            <div>
                <div>
                    <RefreshButtonComponent 
                        disabled={isLoading} 
                        animated={isLoading} 
                        onClick={self.handleRefreshClick} 
                    />
                </div>
                <ErrorsBlockComponent errors={errors} unstyled />
                <div className="fs-xs">Found:&nbsp;{totalCount}</div>
                <table className="agentrunslist newstyle table-striped-even table-hover">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Date</th>
                            <th>Version</th>
                            <th>IP</th>
                            <th>Session Id</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoaded && runs.map(p => {
                            return (
                                <tr key={p.Id}>
                                    <td className="text-right">{p.Id}</td>
                                    <td className="text-nowrap"><DateTimeItem utcDate={p.RunAt} timeZoneId={timeZoneId} format="L LTS (z)" /></td>
                                    <td className="text-nowrap">{p.Version}</td>
                                    <td className="text-nowrap">{p.IP}</td>
                                    <td className="text-nowrap">{p.SessionId}</td>
                                </tr>
                            )
                        })}
                        {isLoading && !isLoaded && <tr><td colSpan={5}><LoadingIndicatorComponent visible /></td></tr>}
                    </tbody>
                </table>
                {(pagesCount > 1) && 
                    <div className="pt-3">
                        <Pagination 
                            className="justify-content-end"
                            pagesCount={pagesCount}
                            pageNum={pageNum}
                            size="sm"
                            disabled={isLoading}
                            onPageSelect={pn => self.setState({pageNum: pn}, self.doLoad)}
                        />
                    </div>
                }
            </div>
        )
    }
}
AgentRunsTable.propTypes = {
    pageSize: PropTypes.number.isRequired,
    timeZoneId: PropTypes.string.isRequired,
}
AgentRunsTable.defaultProps = {
    pageSize: 20,
}

class AgentEventsTable extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            agentId: props.agentId,
            isLoaded: false,
            isLoading: false,
            errors: null,
            events: [],
            requestId: 0,
            pageNum: 1,
            pageSize: props.pageSize,
            totalCount: 0,
            eventTypeId: null,
        }

        this.handleEventTypeChange = this.handleEventTypeChange.bind(this);
        this.handleRefreshClick = this.handleRefreshClick.bind(this);
        this.doLoad = this.doLoad.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps) {
        const self = this;

        if (self.props.agentId != prevProps.agentId) {
            self.setState({agentId: self.props.agentId}, self.doLoad);
        }
        else if (self.props.versionStamp !== prevProps.versionStamp) {
            self.doLoad();
        }
    }

    handleEventTypeChange(e) {
        const self = this;
        const v = parseInt(e.target.value);
        const eventTypeId = isNaN(v) ? null : v;

        if (eventTypeId != self.state.eventTypeId) {
            self.setState({eventTypeId: eventTypeId}, self.doLoad);
        }
    }

    handleRefreshClick() {
        this.doLoad();
    }

    doLoad() {
        const self = this;
        if (!self._isMounted) {
            return;
        }
        const requestId = self.state.requestId + 1;

        const data = {
            AgentId: self.state.agentId, 
            pn: self.state.pageNum, 
            ps: self.state.pageSize, 
            et: self.state.eventTypeId, 
        };

        new Promise(resolve => {
            self.setState({isLoading: true, errors: null, requestId: requestId}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgentEvents',
                            data: data,
                            dataType: 'json',
                            contentType: "application/json",
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted || (self.state.requestId != requestId)
                )
            }
        })
        .then(response => {
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({
                    isLoading: false,
                    isLoaded: true,
                    events: response.Events,
                    pageNum: response.PageNum,
                    pageSize: response.PageSize,
                    totalCount: response.TotalCount
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({isLoading: false, errors: errors})
            }
        });
    }

    render() {
        const self = this;
        const {isLoading, isLoaded, errors, pageNum, pageSize, totalCount, events, eventTypeId} = self.state;
        const {timeZoneId, eventTypes} = self.props;

        const pagesCount = Math.ceil(totalCount / Math.max(pageSize, 1));

        return (
            <div>
                <ErrorsBlockComponent errors={errors} unstyled />
                <div className="d-flex">
                    <div className="form-group newstyle mr-2">
                        <select
                            className="custom-select w-auto"
                            value={eventTypeId == null ? '' : eventTypeId}
                            onChange={self.handleEventTypeChange}
                            disabled={isLoading}
                        >
                            <option value="">any</option>
                            {eventTypes.map(p => <option key={`mt-${p.Id}`} value={p.Id}>{p.Name}</option>)}
                        </select>
                        <label>Event type</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={isLoading} 
                        animated={isLoading} 
                        onClick={self.handleRefreshClick} 
                    />
                </div>
                <div className="fs-xs">Found:&nbsp;{totalCount}</div>
                <table className="agenteventslist newstyle table-striped-even table-hover">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Date</th>
                            <th>Type</th>
                            <th className="text-nowrap">Extended Id</th>
                            <th className="w-100">Message</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoaded && events.map(p => {
                            return (
                                <tr key={p.Id}>
                                    <td className="text-right">{p.Id}</td>
                                    <td className="text-nowrap"><DateTimeItem utcDate={p.CreatedAt} timeZoneId={timeZoneId} format="L LTS (z)" /></td>
                                    <td className="text-nowrap">{p.EventTypeName}</td>
                                    <td className="text-nowrap">{p.ExtendedId}</td>
                                    <td className="w-100"><div className="verticaloverflow height-3 observe-resize">{p.Message}</div></td>
                                </tr>
                            )
                        })}
                        {isLoading && !isLoaded && <tr><td colSpan={5}><LoadingIndicatorComponent visible /></td></tr>}
                    </tbody>
                </table>
                {(pagesCount > 1) && 
                    <div className="pt-3">
                        <Pagination 
                            className="justify-content-end"
                            pagesCount={pagesCount}
                            pageNum={pageNum}
                            size="sm"
                            disabled={isLoading}
                            onPageSelect={pn => self.setState({pageNum: pn}, self.doLoad)}
                        />
                    </div>
                }
            </div>
        )
    }
}
AgentEventsTable.propTypes = {
    pageSize: PropTypes.number.isRequired,
    timeZoneId: PropTypes.string.isRequired,
    eventTypes: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired})).isRequired,
}
AgentEventsTable.defaultProps = {
    pageSize: 20,
}

const AGENTMESSAGE_STATUS = {
    Unknown: 0,
    Pending: 1,
    Expired: 2,
    Success: 3,
    Failed: 4,
}

class AgentMessagesTable extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            agentId: props.agentId,
            isLoaded: false,
            isLoading: false,
            errors: null,
            messages: [],
            requestId: 0,
            pageNum: 1,
            pageSize: props.pageSize,
            totalCount: 0,
            messageTypeId: null,
        }

        this.getRequestData = this.getRequestData.bind(this);
        this.getResponseData = this.getResponseData.bind(this);
        this.handleMessageTypeChange = this.handleMessageTypeChange.bind(this);
        this.handleRefreshClick = this.handleRefreshClick.bind(this);
        this.doLoad = this.doLoad.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps) {
        const self = this;

        if (self.props.agentId != prevProps.agentId) {
            self.setState({agentId: self.props.agentId}, self.doLoad);
        }
        else if (self.props.versionStamp !== prevProps.versionStamp) {
            self.doLoad();
        }
    }

    getRequestData(messageId) {
        const self = this;

        if (self._isMounted && self.state.isLoaded) {
            const message = self.state.messages.find(p => p.Id == messageId);

            if (message != null) {

                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgentMessageRequestData',
                            data: {id: messageId},
                            dataType: 'json',
                            contentType: "application/json",
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                )
                .then(requestResult => {
                    if (self._isMounted) {
                        return `<div class="textualinfo">${requestResult}</div>`;
                    }
                });
            }
        }

        return null;
    }

    getResponseData(messageId) {
        const self = this;

        if (self._isMounted && self.state.isLoaded) {
            const message = self.state.messages.find(p => p.Id == messageId);

            if (message != null) {

                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgentMessageResponseData',
                            data: {id: messageId},
                            dataType: 'json',
                            contentType: "application/json",
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted
                )
                .then(requestResult => {
                    if (self._isMounted) {
                        return `<div class="textualinfo">${requestResult}</div>`;
                    }
                });
            }
        }

        return null;
    }

    handleMessageTypeChange(e) {
        const self = this;
        const v = parseInt(e.target.value);
        const messageTypeId = isNaN(v) ? null : v;

        if (messageTypeId != self.state.messageTypeId) {
            self.setState({messageTypeId: messageTypeId}, self.doLoad);
        }
    }

    handleRefreshClick() {
        this.doLoad();
    }

    doLoad() {
        const self = this;
        if (!self._isMounted) {
            return;
        }
        const requestId = self.state.requestId + 1;

        const data = {
            AgentId: self.state.agentId, 
            pn: self.state.pageNum, 
            ps: self.state.pageSize, 
            mt: self.state.messageTypeId, 
        };

        new Promise(resolve => {
            self.setState({isLoading: true, errors: null, requestId: requestId}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgentMessages',
                            data: data,
                            dataType: 'json',
                            contentType: "application/json",
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted || (self.state.requestId != requestId)
                )
            }
        })
        .then(response => {
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({
                    isLoading: false,
                    isLoaded: true,
                    messages: response.Messages,
                    pageNum: response.PageNum,
                    pageSize: response.PageSize,
                    totalCount: response.TotalCount
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({isLoading: false, errors: errors})
            }
        });
    }

    render() {
        const self = this;
        const {isLoading, isLoaded, errors, pageNum, pageSize, totalCount, messages, messageTypeId} = self.state;
        const {timeZoneId, messageTypes} = self.props;

        const pagesCount = Math.ceil(totalCount / Math.max(pageSize, 1));

        return (
            <div>
                <ErrorsBlockComponent errors={errors} unstyled />
                <div className="d-flex">
                    <div className="form-group newstyle mr-2">
                        <select
                            className="custom-select w-auto"
                            value={messageTypeId == null ? '' : messageTypeId}
                            onChange={self.handleMessageTypeChange}
                            disabled={isLoading}
                        >
                            <option value="">any</option>
                            {messageTypes.map(p => <option key={`mt-${p.Id}`} value={p.Id}>{p.Name}</option>)}
                        </select>
                        <label>Message type</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={isLoading} 
                        animated={isLoading} 
                        onClick={self.handleRefreshClick} 
                    />
                </div>
                <div className="fs-xs">Found:&nbsp;{totalCount}</div>
                <table className="agentmessageslist newstyle table-striped-even table-hover">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Type</th>
                            <th>Request time</th>
                            <th>Valid until</th>
                            <th>Response time</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoaded && messages.map(p => {
                            let rowClass = undefined;

                            switch(p.StatusId) {
                                case AGENTMESSAGE_STATUS.Success:
                                    rowClass = 'success';
                                    break;
                                case AGENTMESSAGE_STATUS.Failed:
                                    rowClass = 'danger';
                                    break;
                                case AGENTMESSAGE_STATUS.Expired:
                                    rowClass = 'secondary';
                                    break;
                            }

                            return (
                                <tr key={p.Id} className={rowClass}>
                                    <td className="text-right">{p.Id}</td>
                                    <td>{p.MessageTypeName}</td>
                                    <td className="text-nowrap">
                                        <ItemPopupDescriptionComponent
                                            popoverClassName="popover-white"
                                            placement="bottom"
                                            text={() => self.getRequestData(p.Id)}
                                            showCopyButton
                                            showIfEmpty
                                        >
                                            <DateTimeItem utcDate={p.CreatedAt} timeZoneId={timeZoneId} format="L LTS (z)" />
                                        </ItemPopupDescriptionComponent>
                                    </td>
                                    <td className="text-nowrap"><DateTimeItem utcDate={p.RequestValidUntil} timeZoneId={timeZoneId} format="L LTS (z)" /></td>
                                    <td className="text-nowrap">
                                    {Boolean(p.ResponsedAt) &&
                                        <ItemPopupDescriptionComponent
                                            popoverClassName="popover-white"
                                            placement="bottom"
                                            text={() => self.getResponseData(p.Id)}
                                            showCopyButton
                                            showIfEmpty
                                        >
                                            <DateTimeItem utcDate={p.ResponsedAt} timeZoneId={timeZoneId} format="L LTS (z)" />
                                        </ItemPopupDescriptionComponent>
                                    }
                                    </td>
                                    <td>{p.StatusName}</td>
                                </tr>
                            )
                        })}
                        {isLoading && !isLoaded && <tr><td colSpan={5}><LoadingIndicatorComponent visible /></td></tr>}
                    </tbody>
                </table>
                {(pagesCount > 1) && 
                    <div className="pt-3">
                        <Pagination 
                            className="justify-content-end"
                            pagesCount={pagesCount}
                            pageNum={pageNum}
                            size="sm"
                            disabled={isLoading}
                            onPageSelect={pn => self.setState({pageNum: pn}, self.doLoad)}
                        />
                    </div>
                }
            </div>
        )
    }
}
AgentMessagesTable.propTypes = {
    pageSize: PropTypes.number.isRequired,
    timeZoneId: PropTypes.string.isRequired,
    messageTypes: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired})).isRequired,
}
AgentMessagesTable.defaultProps = {
    pageSize: 20,
}

class AgentFilesTable extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            agentId: props.agentId,
            isLoaded: false,
            isLoading: false,
            errors: null,
            files: [],
            requestId: 0,
            pageNum: 1,
            pageSize: props.pageSize,
            totalCount: 0,
        }

        this.doLoad = this.doLoad.bind(this);
        this.doDeleteFile = this.doDeleteFile.bind(this);
        this.handleDeleteClick = this.handleDeleteClick.bind(this);
        this.handleRefreshClick = this.handleRefreshClick.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps) {
        const self = this;

        if (self.props.agentId != prevProps.agentId) {
            self.setState({agentId: self.props.agentId}, self.doLoad);
        }
        else if (self.props.versionStamp !== prevProps.versionStamp) {
            self.doLoad();
        }
    }

    handleRefreshClick() {
        this.doLoad();
    }

    doLoad() {
        const self = this;
        if (!self._isMounted) {
            return;
        }
        const requestId = self.state.requestId + 1;

        const data = {
            AgentId: self.state.agentId, 
            pn: self.state.pageNum, 
            ps: self.state.pageSize, 
        };

        new Promise(resolve => {
            self.setState({isLoading: true, errors: null, requestId: requestId}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgentFiles',
                            data: data,
                            dataType: 'json',
                            contentType: "application/json",
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted || (self.state.requestId != requestId)
                )
            }
        })
        .then(response => {
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({
                    isLoading: false,
                    isLoaded: true,
                    files: response.Files,
                    pageNum: response.PageNum,
                    pageSize: response.PageSize,
                    totalCount: response.TotalCount
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({isLoading: false, errors: errors})
            }
        });
    }

    doDeleteFile(url) {
        const self = this;
        const {verificationToken} = self.props;;

        const result = MakeApiCall(
            $.ajax(
                {
                    url: url,
                    type: 'DELETE',
                    headers: {RequestVerificationToken: verificationToken},
                    data: null,
                }
            )
        )
        .catch(errors => DisplayErrors(errors))
        .finally(self.doLoad);

        return result;
    }

    handleDeleteClick(e) {
        const url = e.currentTarget.getAttribute('data-url');

        if (Boolean(url)) {
            this.doDeleteFile(url);
        }
    }

    render() {
        const self = this;
        const {isLoading, isLoaded, errors, pageNum, pageSize, totalCount, files} = self.state;
        const {timeZoneId} = self.props;

        const pagesCount = Math.ceil(totalCount / Math.max(pageSize, 1));

        return (
            <div>
                <div>
                    <RefreshButtonComponent 
                        disabled={isLoading} 
                        animated={isLoading} 
                        onClick={self.handleRefreshClick} 
                    />
                </div>
                <ErrorsBlockComponent errors={errors} unstyled />
                <div className="fs-xs">Found:&nbsp;{totalCount}</div>
                <table className="agentfileslist newstyle table-striped-even table-hover">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Name</th>
                            <th>Size</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoaded && files.map(p => {
                            return (
                                <tr key={p.Url}>
                                    <td className="text-nowrap"><DateTimeItem utcDate={p.CreatedAt} timeZoneId={timeZoneId} /></td>
                                    <td className="w-100 text-break-word">
                                        <Link 
                                            to={p.Url}
                                            target="_blank"
                                        >
                                            {p.Name}
                                        </Link>
                                    </td>
                                    <td className="text-right text-nowrap">{HumanFileSize(p.Length)}</td>
                                    <td>
                                        <button 
                                            type="button" 
                                            data-url={p.Url} 
                                            onClick={self.handleDeleteClick}
                                            className="text-danger likelink"
                                        >
                                            delete
                                        </button>
                                    </td>
                                </tr>
                            )
                        })}
                        {isLoading && !isLoaded && <tr><td colSpan={5}><LoadingIndicatorComponent visible /></td></tr>}
                    </tbody>
                </table>
                {(pagesCount > 1) && 
                    <div className="pt-3">
                        <Pagination 
                            className="justify-content-end"
                            pagesCount={pagesCount}
                            pageNum={pageNum}
                            size="sm"
                            disabled={isLoading}
                            onPageSelect={pn => self.setState({pageNum: pn}, self.doLoad)}
                        />
                    </div>
                }
            </div>
        )
    }
}
AgentFilesTable.propTypes = {
    pageSize: PropTypes.number.isRequired,
    timeZoneId: PropTypes.string.isRequired,
    verificationToken: PropTypes.string,
}
AgentFilesTable.defaultProps = {
    pageSize: 20,
}

class AgentAppEventsTable extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            agentId: props.agentId,
            isLoaded: false,
            isLoading: false,
            errors: null,
            events: [],
            requestId: 0,
            pageNum: 1,
            pageSize: props.pageSize,
            totalCount: 0,
            eventTypeId: null,
        }

        this.handleEventTypeChange = this.handleEventTypeChange.bind(this);
        this.handleRefreshClick = this.handleRefreshClick.bind(this);
        this.doLoad = this.doLoad.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps) {
        const self = this;

        if (self.props.agentId != prevProps.agentId) {
            self.setState({agentId: self.props.agentId}, self.doLoad);
        }
        else if (self.props.versionStamp !== prevProps.versionStamp) {
            self.doLoad();
        }
    }

    handleEventTypeChange(e) {
        const self = this;
        const v = parseInt(e.target.value);
        const eventTypeId = isNaN(v) ? null : v;

        if (eventTypeId != self.state.eventTypeId) {
            self.setState({eventTypeId: eventTypeId}, self.doLoad);
        }
    }

    handleRefreshClick() {
        this.doLoad();
    }

    doLoad() {
        const self = this;
        if (!self._isMounted) {
            return;
        }
        const requestId = self.state.requestId + 1;

        const data = {
            AgentId: self.state.agentId, 
            pn: self.state.pageNum, 
            ps: self.state.pageSize, 
            et: self.state.eventTypeId, 
        };

        new Promise(resolve => {
            self.setState({isLoading: true, errors: null, requestId: requestId}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgentAppEvents',
                            data: data,
                            dataType: 'json',
                            contentType: "application/json",
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted || (self.state.requestId != requestId)
                )
            }
        })
        .then(response => {
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({
                    isLoading: false,
                    isLoaded: true,
                    events: response.Events,
                    pageNum: response.PageNum,
                    pageSize: response.PageSize,
                    totalCount: response.TotalCount
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({isLoading: false, errors: errors})
            }
        });
    }

    render() {
        const self = this;
        const {isLoading, isLoaded, errors, pageNum, pageSize, totalCount, events, eventTypeId} = self.state;
        const {timeZoneId, eventTypes} = self.props;

        const pagesCount = Math.ceil(totalCount / Math.max(pageSize, 1));

        const renderParams = eventData => {
            if ((eventData?.Params == null) || (eventData.Params.length == 0)) {
                return null;
            }

            const content = eventData.Params
                .map(p => `${p.Name} = ${p.Value}`)
                .join('\n');

            return `<div class="textualinfo">${content}</div>`;
        }

        return (
            <div>
                <div className="d-flex">
                    <div className="form-group newstyle mr-2">
                        <select
                            className="custom-select w-auto"
                            value={eventTypeId == null ? '' : eventTypeId}
                            onChange={self.handleEventTypeChange}
                            disabled={isLoading}
                        >
                            <option value="">any</option>
                            {eventTypes.map(p => <option key={`mt-${p.Id}`} value={p.Id}>{p.Name}</option>)}
                        </select>
                        <label>Event type</label>
                    </div>
                    <RefreshButtonComponent 
                        disabled={isLoading} 
                        animated={isLoading} 
                        onClick={self.handleRefreshClick} 
                    />
                </div>
                <ErrorsBlockComponent errors={errors} unstyled />
                <div className="fs-xs">Found:&nbsp;{totalCount}</div>
                <table className="agenteventslist newstyle table-striped-even table-hover">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Version</th>
                            <th className="w-100">Message</th>
                            <th>Information</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoaded && events.map(p => {
                            let rowClass = undefined;

                            switch (p.EventTypeId) {
                                case 1:
                                case 5:
                                    rowClass = 'danger';
                                    break;
                                case 2:
                                    rowClass = 'warning';
                                    break;
                            }

                            return (
                                <tr key={p.Id} className={rowClass}>
                                    <td className="text-right">{p.Id}</td>
                                    <td className="text-nowrap"><DateTimeItem utcDate={p.CreatedAt} timeZoneId={timeZoneId} format="L LTS (z)" /></td>
                                    <td className="text-nowrap">{`${p.EventTypeName} (#${p.EventId})`}</td>
                                    <td className="text-nowrap">{p.AgentVersion}</td>
                                    <td className="w-100">
                                        <div className="verticaloverflow height-3 observe-resize">{p.Message}</div>
                                    </td>
                                    <td className="text-nowrap fs-sm">
                                        {Boolean(p.Details) && 
                                            <div>
                                                <ItemPopupDescriptionComponent
                                                    popoverClassName="popover-white"
                                                    placement="bottom"
                                                    text={`<div class="textualinfo">${p.Details}</div>`}
                                                    showCopyButton
                                                >
                                                    Details
                                                </ItemPopupDescriptionComponent>
                                            </div>
                                        }
                                        {Boolean(p.MessageTemplate) && 
                                            <div>
                                                <ItemPopupDescriptionComponent
                                                    popoverClassName="popover-white"
                                                    placement="bottom"
                                                    text={`<div class="textualinfo">${p.MessageTemplate}</div>`}
                                                    showCopyButton
                                                >
                                                    Message template
                                                </ItemPopupDescriptionComponent>
                                            </div>
                                        }
                                        {Boolean(p.StackTrace) && 
                                            <div>
                                                <ItemPopupDescriptionComponent
                                                    popoverClassName="popover-white"
                                                    placement="bottom"
                                                    text={`<div class="textualinfo">${p.StackTrace}</div>`}
                                                    showCopyButton
                                                >
                                                    Stack trace
                                                </ItemPopupDescriptionComponent>
                                            </div>
                                        }
                                        {Boolean(p.Params) && (p.Params.length > 0) &&
                                            <div>
                                                <ItemPopupDescriptionComponent
                                                    popoverClassName="popover-white"
                                                    placement="bottom"
                                                    text={() => renderParams(p)}
                                                    showCopyButton
                                                >
                                                    Parameters
                                                </ItemPopupDescriptionComponent>
                                            </div>
                                        }
                                    </td>
                                </tr>
                            )
                        })}
                        {isLoading && !isLoaded && <tr><td colSpan={5}><LoadingIndicatorComponent visible /></td></tr>}
                    </tbody>
                </table>
                {(pagesCount > 1) && 
                    <div className="pt-3">
                        <Pagination 
                            className="justify-content-end"
                            pagesCount={pagesCount}
                            pageNum={pageNum}
                            size="sm"
                            disabled={isLoading}
                            onPageSelect={pn => self.setState({pageNum: pn}, self.doLoad)}
                        />
                    </div>
                }
            </div>
        )
    }
}
AgentAppEventsTable.propTypes = {
    pageSize: PropTypes.number.isRequired,
    timeZoneId: PropTypes.string.isRequired,
    eventTypes: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired})).isRequired,
}
AgentAppEventsTable.defaultProps = {
    pageSize: 20,
}

export class AgentInfoComponent extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            agentId: props.agentId,
            isLoaded: false,
            action: AGENTINFO_ACTION.NONE,
            isLoading: false,
            errors: null,
            agentInfo: null,
            requestId: 0,
            versionStamp: {},
            activeAgentRequests: [],
        }

        this.refreshCodeMirror = this.refreshCodeMirror.bind(this);
        this.doLoad = this.doLoad.bind(this);
        this.doSaveNotes = this.doSaveNotes.bind(this);
        this.doRefresh = this.doRefresh.bind(this);
        this.doSendAgentRequest = this.doSendAgentRequest.bind(this);
        this.handleRefreshClick = this.handleRefreshClick.bind(this);
        this.handleGoAgentClick = this.handleGoAgentClick.bind(this);
        this.handleEditNotesDialogHide = this.handleEditNotesDialogHide.bind(this);
        this.handleEditNotesClick = this.handleEditNotesClick.bind(this);
        this.handleActivateLoggingClick = this.handleActivateLoggingClick.bind(this);
        this.handleDeactivateLoggingClick = this.handleDeactivateLoggingClick.bind(this);
        this.handleCleanupLogsClick = this.handleCleanupLogsClick.bind(this);
        this.handleGetLogsClick = this.handleGetLogsClick.bind(this);
        this.handleGetEventLogsClick = this.handleGetEventLogsClick.bind(this);
        this.handleGetRoutinesDatabaseClick = this.handleGetRoutinesDatabaseClick.bind(this);
        this.handleGetContextDatabaseClick = this.handleGetContextDatabaseClick.bind(this);

        this.handleSendMessageClick = this.handleSendMessageClick.bind(this);
        this.handleRequestPropsDialogHide = this.handleRequestPropsDialogHide.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();

        $(document).on('shown.bs.tab', '.nav-tabs a', self.refreshCodeMirror);
    }

    componentWillUnmount() {
        const self = this;

        self._isMounted = false;
        $(document).off('shown.bs.tab', '.nav-tabs a', self.refreshCodeMirror);
    }

    refreshCodeMirror(e) {
        const tabId = e.target.getAttribute('href');
        $(tabId).find('.CodeMirror').get(0)?.CodeMirror.refresh();
    }

    doLoad() {
        const self = this;

        if (!self._isMounted) {
            return;
        }

        const requestId = self.state.requestId + 1;

        const data = {AgentId: self.state.agentId, ListSource: self.props.listSource};

        new Promise(resolve => {
            self.setState({action: AGENTINFO_ACTION.LOADING, errors: null, requestId: requestId}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getAgentInfo',
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted || (self.state.requestId != requestId)
                )
            }
        })
        .then(response => {
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({
                    action: AGENTINFO_ACTION.NONE,
                    isLoaded: true,
                    agentInfo: response,
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({action: AGENTINFO_ACTION.NONE, errors: errors})
            }
        });
    }

    doSendAgentRequest(messageTypeId, requestData, validSeconds) {
        const self = this;

        if (!self._isMounted) {
            return;
        }

        const activeAgentRequests = self.state.activeAgentRequests.slice().concat([messageTypeId]);
        const verificationToken = self.state.agentInfo.VerificationToken;
        const data = {
            AgentId: self.state.agentId, 
            AgentMessageTypeId: messageTypeId, 
            ValidSeconds: parseInt(validSeconds) || null, 
            RequestData: requestData //JSON.stringify(requestData || {})
        };

        new Promise(resolve => self.setState({activeAgentRequests: activeAgentRequests}, resolve))
            .then(() => {
                if (self._isMounted) {
                    return MakeApiCall(
                        $.post(
                            {
                                url: '/api/admin/sendAgentMessage',
                                headers: {RequestVerificationToken: verificationToken},
                                data: data,
                                data: JSON.stringify(data),
                                contentType: 'application/json',
                                dataType: 'json',
                                cache: false
                            }
                        ),
                        false,
                        () => !self._isMounted
                    )
                }
            })
            .then(() => {
                if (self._isMounted) {
                    DisplaySuccessMessage('Request sucessfilly sended. Check reqults in Messages tab.');
                }
            })
            .catch(errors => {
                if (self._isMounted) {
                    DisplayErrors(errors);
                }
            })
            .finally(() => {
                if (self._isMounted) {
                    self.setState({activeAgentRequests: self.state.activeAgentRequests.filter(p => p != messageTypeId)}, self.doRefresh);
                }
            })
    }

    doRefresh() {
        const self = this;

        if (self._isMounted) {
            self.setState({versionStamp: new Date() * 1}, self.doLoad);
        }
    }

    handleRefreshClick() {
        this.doRefresh();
    }

    handleGoAgentClick(e) {
        const self = this;

        const agentId = parseInt(e.currentTarget.getAttribute('data-id'));

        if (self._isMounted && !isNaN(agentId)) {

            ReplaceUrl(UrlBuilder.AgentInfo({AgentId: agentId, ListSource: self.props.listSource}));
    
            self.setState({agentId: agentId}, self.doLoad);
        }
    }

    doSaveNotes(notes) {
        const self = this;
        const data = {AgentId: self.state.agentId, Notes: notes};
        const verificationToken = self.state.agentInfo.VerificationToken;

        const result = MakeApiCall(
            $.post(
                {
                    url: '/api/admin/saveAgentNotes',
                    headers: {RequestVerificationToken: verificationToken},
                    data: JSON.stringify(data),
                    contentType: 'application/json',
                    dataType: 'json',
                    cache: false
                }
            )
        )
        .then(response => {
            if (self._isMounted) {
                const agentInfo = {...self.state.agentInfo, AdminNotes: notes};
                self.setState({agentInfo: agentInfo});
            }

            return response;
        });

        return result;
    }

    handleEditNotesClick() {
        this.setState({action: AGENTINFO_ACTION.EDITNOTES});
    }

    handleEditNotesDialogHide() {
        const self = this;

        if (self.state.action == AGENTINFO_ACTION.EDITNOTES) {
            self.setState({action: AGENTINFO_ACTION.NONE});
        }
    }

    handleGetLogsClick() {
        const self = this;

        self.doSendAgentRequest(AGENTMESSAGE_TYPE.UploadLogs, {}, 5 * 60);
    }

    handleGetEventLogsClick() {
        const self = this;

        self.doSendAgentRequest(AGENTMESSAGE_TYPE.UploadEventLogs, {}, 5 * 60);
    }

    handleGetRoutinesDatabaseClick() {
        const self = this;

        self.doSendAgentRequest(AGENTMESSAGE_TYPE.UploadRoutinesDataBase, {}, 5 * 60);
    }

    handleGetContextDatabaseClick() {
        const self = this;

        self.doSendAgentRequest(AGENTMESSAGE_TYPE.UploadContextDataBase, {}, 5 * 60);
    }

    handleActivateLoggingClick() {
        const self = this;

        self.doSendAgentRequest(AGENTMESSAGE_TYPE.ActivateLogging, {}, 60);
    }

    handleDeactivateLoggingClick() {
        const self = this;

        self.doSendAgentRequest(AGENTMESSAGE_TYPE.DeactivateLogging, {}, 60);
    }

    handleCleanupLogsClick() {
        const self = this;

        self.doSendAgentRequest(AGENTMESSAGE_TYPE.CleanupLog, {}, 60);
    }

    handleSendMessageClick(e) {
        const messageTypeId = parseInt(e.currentTarget.getAttribute('data-id'));

        if (!isNaN(messageTypeId) && Object.keys(AGENTMESSAGE_TYPE).some(p => AGENTMESSAGE_TYPE[p] === messageTypeId)) {
            const self = this;

            const requestParams = AGENTMESSAGE_TYPE.Params[messageTypeId] || [];

            if (requestParams.length > 0) {

                const requestProps = requestParams.map(p => {
                    return {
                        Key: p.Id,
                        Value: p.DefaultValue,
                        JobOption: p
                    }
                });

                self.setState({
                    action: AGENTINFO_ACTION.REQUESTPROPS_DLG,  
                    messageTypeId: messageTypeId, 
                    requestProps: requestProps
                })
            }
            else {
                const timeOut = AGENTMESSAGE_TYPE.Timeout[messageTypeId] || AGENTMESSAGE_TYPE.Timeout.Default;
                self.doSendAgentRequest(messageTypeId, {}, timeOut);
            }
        }
    }

    handleRequestPropsDialogHide(requestValues) {
        const self = this;
        
        if (self._isMounted) {

            if (Boolean(requestValues)) {
                const messageTypeId = self.state.messageTypeId;
                const timeOut = AGENTMESSAGE_TYPE.Timeout[messageTypeId] || AGENTMESSAGE_TYPE.Timeout.Default;
                const data = {};
                self.state.requestProps.forEach(p => data[p.Key] = p.Value);
                requestValues.forEach(p => data[p.Key] = p.Value);

                new Promise(resolve => self.setState({action: AGENTINFO_ACTION.NONE}, resolve))
                    .then(() => {
                        if (self._isMounted) {
                            self.doSendAgentRequest(messageTypeId, data, timeOut);
                        }
                    })
            }
            else {
                self.setState({action: AGENTINFO_ACTION.NONE});
            }
        }
    }

    render() {
        const self = this;
        const {agentId, agentInfo, action, isLoaded, errors, versionStamp, activeAgentRequests} = self.state;
        const {timeZoneId, serverTypes, jobTypes, deletedStates, usageStates, eventTypes, appEventTypes, messageTypes} = self.props;

        const isDisabled = !isLoaded || (action != AGENTINFO_ACTION.NONE);

        const buttonPrev = Boolean(agentInfo) &&
            <Link 
                key="buttonPrev"
                className="btn btn-outline-secondary btn-sm newstyle" 
                disabled={isDisabled || !Boolean(agentInfo.PrevId)} 
                data-id={agentInfo.PrevId}
                to={self.handleGoAgentClick}
            >
                Prev
            </Link>
        
        const buttonNext = Boolean(agentInfo) &&
            <Link 
                key="buttonNest"
                className="btn btn-outline-secondary btn-sm newstyle" 
                disabled={isDisabled || !Boolean(agentInfo.NextId)} 
                data-id={agentInfo.NextId}
                to={self.handleGoAgentClick}
            >
                Next
            </Link>

        const operationSystemIcon = APPLICATION_TYPE.getOSIconClassName(agentInfo?.ApplicationTypeId);

        return (
            <div>
                <PageHeader
                    fixed
                    captionTruncate
                    notWrappedMaxHeight={75}
                    caption={
                        <h2 className="text-truncate">                                    
                            <span className="mr-2">Agent #{agentId}</span>
                            <RefreshButtonComponent 
                                disabled={action == AGENTINFO_ACTION.LOADING} 
                                animated={action == AGENTINFO_ACTION.LOADING} 
                                onClick={self.handleRefreshClick} 
                                className="fs-085"
                            />
                        </h2>
                    }
                    buttons={[<div key="buttonGroup" className="btn-group">{[buttonPrev, buttonNext].filter(Boolean)}</div>]}
                />
                <ErrorsBlockComponent errors={errors} />
                {isLoaded && (action == AGENTINFO_ACTION.EDITNOTES) &&
                    <EditNotesDialogComponent
                        title={<span className="fs-125"><span className="mr-1 fa fa-edit" />Notes for agent #{agentInfo.Id}</span>}
                        notes={agentInfo.AdminNotes}
                        doSave={self.doSaveNotes}
                        onHide={self.handleEditNotesDialogHide}
                    />
                }
                {isLoaded && (action == AGENTINFO_ACTION.REQUESTPROPS_DLG) && 
                    <JobOptionsDlgComponent
                        id="RequestDataDialog"
                        serverTypeId={0}
                        options={self.state.requestProps}
                        title="Request data"
                        onHide={self.handleRequestPropsDialogHide}
                        strings={{
                            BTN_SaveAndClose: 'Send request',
                        }}
                        newStyle
                    />
                }
                {isLoaded &&
                    <div>
                        <InfoItem 
                            caption="Account" 
                            content={
                                <AccountComponent 
                                    id={agentInfo.AccountId} 
                                    name={agentInfo.AccountName} 
                                    disabled={isDisabled} 
                                    showId 
                                    showLoginAs 
                                    linkToInfo 
                                    url={UrlBuilder.AgentView({AgentId: agentInfo.Id})} 
                                    loginAsTooltip={{title: `LogIn as ${agentInfo.AccountName} and go to agent's page`}} 
                                />
                            } 
                        />
                        <InfoItem caption="AgentKey" content={agentInfo.AgentKey} />
                        <InfoItem caption="Name" content={agentInfo.Name} />
                        <InfoItem 
                            caption="Type" 
                            content={
                                <>
                                    {Boolean(operationSystemIcon) && <span className={operationSystemIcon + ' mr-2'} />}
                                    <span>{agentInfo.ApplicationTypeName}</span>
                                </>
                            } 
                        />
                        <InfoItem caption="Version" content={agentInfo.DisplayVersion} />
                        <InfoItem caption="Created" content={<DateTimeItem utcDate={agentInfo.CreatedAt} timeZoneId={timeZoneId} format="L LTS (z)" />} />
                        {Boolean(agentInfo.DeletedAt) &&
                            <InfoItem 
                                caption="Status" 
                                content={
                                    <div className="text-danger">
                                        <span className="mr-2">deleted</span>
                                        <DateTimeItem utcDate={agentInfo.DeletedAt} timeZoneId={timeZoneId} format="L LTS (z)" />
                                    </div>
                                } 
                            />
                        }
                        {!Boolean(agentInfo.DeletedAt) && !agentInfo.IsActive &&
                            <InfoItem caption="Status" content={<span className="text-danger">disconnected</span>} />
                        }
                        {!Boolean(agentInfo.DeletedAt) && agentInfo.IsActive && !agentInfo.IsOnline &&
                            <InfoItem 
                                caption="Status" 
                                content={
                                    <div className="text-danger">
                                        <span className="mr-2">offline from</span>
                                        <DateTimeItem utcDate={agentInfo.LastRequestAt} timeZoneId={timeZoneId} format="L LTS (z)" />
                                    </div>
                                } 
                            />
                        }
                        {!Boolean(agentInfo.DeletedAt) && agentInfo.IsActive && agentInfo.IsOnline &&
                            <InfoItem caption="Status" content={<span className="text-success">online</span>} />
                        }
                        <InfoItem caption="Last IP" content={agentInfo.LastIP} />
                        <InfoItem caption="Update channel" content={agentInfo.UpdateChannelName} />
                        <InfoItem 
                            vertical
                            caption="Notes" 
                            content={
                                <textarea
                                    className="form-control compact text-body bg-white"
                                    readOnly
                                    value={agentInfo.AdminNotes || ''}
                                />
                            }
                        />
                        <div className="pt-4">
                            <button 
                                type="button"
                                className="btn btn-outline-primary btn-sm newstyle"
                                disabled={isDisabled}
                                onClick={self.handleEditNotesClick}
                            >
                                <span className="mr-1 fa fa-edit" />Edit notes
                            </button>
                        </div>
                   </div>
                }
                {isLoaded &&
                    <div className="pt-3">
                        <ul className="nav nav-tabs" role="tablist">
                            <li className="nav-item">
                                <Link className="nav-link active" data-toggle="tab" to="#connections">
                                    DBMS connections
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link className="nav-link" data-toggle="tab" to="#jobs">
                                    Jobs
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link className="nav-link" data-toggle="tab" to="#runs">
                                    Runs
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link className="nav-link" data-toggle="tab" to="#events">
                                    Events
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link className="nav-link" data-toggle="tab" to="#messages">
                                    Messages
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link className="nav-link" data-toggle="tab" to="#appevents">
                                    App events
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link className="nav-link" data-toggle="tab" to="#files">
                                    Files
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link className="nav-link" data-toggle="tab" to="#systeminfo">
                                    System info
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link className="nav-link" data-toggle="tab" to="#interactions">
                                    Interactions
                                </Link>
                            </li>
                        </ul>
                        <div className="tab-content">
                            <div id="connections" className="container tab-pane active p-0">
                                <ConnectionsTable 
                                    agentId={agentInfo.Id}
                                    timeZoneId={timeZoneId}
                                    versionStamp={versionStamp}
                                    serverTypes={serverTypes}
                                    deletedStates={deletedStates}
                                    usageStates={usageStates}
                                />
                            </div>
                            <div id="jobs" className="container tab-pane p-0">
                                <JobsTable 
                                    accountId={agentInfo.AccountId}
                                    accountName={agentInfo.AccountName}
                                    agentId={agentInfo.Id}
                                    timeZoneId={timeZoneId}
                                    versionStamp={versionStamp}
                                    jobTypes={jobTypes}
                                    deletedStates={deletedStates}
                                />
                            </div>
                            <div id="runs" className="container tab-pane p-0">
                                <AgentRunsTable 
                                    agentId={agentInfo.Id}
                                    timeZoneId={timeZoneId}
                                    versionStamp={versionStamp}
                                />
                            </div>
                            <div id="events" className="container tab-pane p-0">
                                <AgentEventsTable 
                                    agentId={agentInfo.Id}
                                    timeZoneId={timeZoneId}
                                    versionStamp={versionStamp}
                                    eventTypes={eventTypes}
                                />
                            </div>
                            <div id="messages" className="container tab-pane p-0">
                                <AgentMessagesTable 
                                    agentId={agentInfo.Id}
                                    timeZoneId={timeZoneId}
                                    versionStamp={versionStamp}
                                    messageTypes={messageTypes}
                                />
                            </div>
                            <div id="appevents" className="container tab-pane p-0">
                                <AgentAppEventsTable 
                                    agentId={agentInfo.Id}
                                    timeZoneId={timeZoneId}
                                    versionStamp={versionStamp}
                                    eventTypes={appEventTypes}
                                />
                            </div>
                            <div id="files" className="container tab-pane p-0">
                                <AgentFilesTable 
                                    agentId={agentInfo.Id}
                                    timeZoneId={timeZoneId}
                                    versionStamp={versionStamp}
                                    verificationToken={agentInfo.VerificationToken}
                                />
                            </div>
                            <div id="systeminfo" className="tab-pane pt-2">
                                <div className="pb-3">
                                    <InfoItem 
                                        caption="Time Zone" 
                                        content={Boolean(agentInfo.TimeZoneId) && GetMomentTimeZoneName(agentInfo.TimeZoneId, GetMomentTimeZoneName.FLAG_ADD_UTCOFFSET, agentInfo.TimeZoneName)}
                                    />
                                    {(agentInfo.ApplicationTypeId == APPLICATION_TYPE.SqlBakForWindows) &&
                                        <InfoItem 
                                            caption="Latest .NET Framework version" 
                                            content={agentInfo.MaxDotNetVersion}
                                        />
                                    }
                                </div>
                                <div className="code-control autoheight max-rows-15 border-secondary">
                                    <CodeMirror
                                        options={{
                                            lineNumbers: false, 
                                            autoRefresh: false, 
                                            lineSeparator: '\r\n', 
                                            mode: 'xml', 
                                            readOnly: true
                                        }}
                                        value={agentInfo.SystemInfo|| ''} 
                                    />
                                </div>
                            </div>
                            <div id="interactions" className="container tab-pane p-0">
                                <div className="form-buttons mt-3">
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm newstyle"
                                        data-id={AGENTMESSAGE_TYPE.ActivateLogging}
                                        onClick={self.handleSendMessageClick}
                                        disabled={isDisabled || activeAgentRequests.some(p => p == AGENTMESSAGE_TYPE.ActivateLogging)}
                                    >
                                        Activate logging
                                        <ButtonBusyIndicatorComponent visible={activeAgentRequests.some(p => p == AGENTMESSAGE_TYPE.ActivateLogging)} />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm newstyle"
                                        data-id={AGENTMESSAGE_TYPE.DeactivateLogging}
                                        onClick={self.handleSendMessageClick}
                                        disabled={isDisabled || activeAgentRequests.some(p => p == AGENTMESSAGE_TYPE.DeactivateLogging)}
                                    >
                                        Deactivate logging
                                        <ButtonBusyIndicatorComponent visible={activeAgentRequests.some(p => p == AGENTMESSAGE_TYPE.DeactivateLogging)} />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm newstyle"
                                        data-id={AGENTMESSAGE_TYPE.CleanupLog}
                                        onClick={self.handleSendMessageClick}
                                        disabled={isDisabled || activeAgentRequests.some(p => p == AGENTMESSAGE_TYPE.CleanupLog)}
                                    >
                                        Cleanup logs
                                        <ButtonBusyIndicatorComponent visible={activeAgentRequests.some(p => p == AGENTMESSAGE_TYPE.CleanupLog)} />
                                    </button>
                                </div>
                                <div className="form-buttons mt-3">
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm newstyle"
                                        data-id={AGENTMESSAGE_TYPE.UploadLogs}
                                        onClick={self.handleSendMessageClick}
                                        disabled={isDisabled || activeAgentRequests.some(p => p == AGENTMESSAGE_TYPE.UploadLogs)}
                                    >
                                        Request agent logs
                                        <ButtonBusyIndicatorComponent visible={activeAgentRequests.some(p => p == AGENTMESSAGE_TYPE.UploadLogs)} />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm newstyle"
                                        data-id={AGENTMESSAGE_TYPE.UploadEventLogs}
                                        onClick={self.handleSendMessageClick}
                                        disabled={isDisabled || activeAgentRequests.some(p => p == AGENTMESSAGE_TYPE.UploadEventLogs)}
                                    >
                                        Request event logs
                                        <ButtonBusyIndicatorComponent visible={activeAgentRequests.some(p => p == AGENTMESSAGE_TYPE.UploadEventLogs)} />
                                    </button>
                                </div>
                                <div className="form-buttons mt-3">
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm newstyle"
                                        data-id={AGENTMESSAGE_TYPE.UploadRoutinesDataBase}
                                        onClick={self.handleSendMessageClick}
                                        disabled={isDisabled || activeAgentRequests.some(p => p == AGENTMESSAGE_TYPE.UploadRoutinesDataBase)}
                                    >
                                        Request Routines database
                                        <ButtonBusyIndicatorComponent visible={activeAgentRequests.some(p => p == AGENTMESSAGE_TYPE.UploadRoutinesDataBase)} />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm newstyle"
                                        data-id={AGENTMESSAGE_TYPE.UploadContextDataBase}
                                        onClick={self.handleSendMessageClick}
                                        disabled={isDisabled || activeAgentRequests.some(p => p == AGENTMESSAGE_TYPE.UploadContextDataBase)}
                                    >
                                        Request Context database
                                        <ButtonBusyIndicatorComponent visible={activeAgentRequests.some(p => p == AGENTMESSAGE_TYPE.UploadContextDataBase)} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </div>
        )
    }
}
AgentInfoComponent.propTypes = {
    agentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    serverTypes: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired})).isRequired,
    jobTypes: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired})).isRequired,
    deletedStates: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired})).isRequired,
    usageStates: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired})).isRequired,
    eventTypes: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired})).isRequired,
    messageTypes: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired})).isRequired,
    appEventTypes: PropTypes.arrayOf(PropTypes.shape({Id: PropTypes.number.isRequired, Name: PropTypes.string.isRequired})).isRequired,
    timeZoneId: PropTypes.string.isRequired,
    listSource: PropTypes.string,
}
